/**
 * Where the rock belt sits, and how far back the camera stands to hold it.
 *
 * These numbers are a pair. The belt is placed just outside the outermost
 * planet — far enough out to be plainly not a planet, close enough in that
 * the whole ring is inside the frame — and the camera's reach is chosen so
 * that it is. Move one without the other and rocks drift off the edge of the
 * window, which means a task you have been told about and cannot see.
 *
 * They live here, apart from the components that use them, so the claim can
 * be checked rather than eyeballed: see framing.test.ts, which projects the
 * worst case of the belt at every plausible window shape.
 */
export const ORBIT_INNER = 3.2;
export const ORBIT_STEP = 2.15;

/** The belt, as a multiple of the outermost orbit's radius. */
export const BELT_INNER = 1.01;
export const BELT_SPREAD = 0.08;
/** How far above or below the plane of the planets a rock may hang. */
export const BELT_HEIGHT = 0.14;
/** The gentle hover, on top of that. */
export const BELT_BOB = 0.012;
/** How far the camera reaches past the outermost orbit at full zoom-out. */
export const CAMERA_REACH = 1.6;

export const FIELD_OF_VIEW = 38;
/** The camera rides this far above the plane, as a fraction of its distance. */
export const CAMERA_LIFT = 0.52;
/** However small the system, the camera never comes closer than this. */
export const CAMERA_FLOOR = 7;

/**
 * How much further back a narrow window has to stand.
 *
 * This used to switch on at exactly 1.0, so a window a pixel wider than it
 * was tall framed the system quite differently from one a pixel narrower, and
 * anything squarer than about 4:3 lost the sides of the belt off the edges.
 * 1.37 is where the horizontal field becomes the binding one for a ring of
 * this radius at this field of view; above it nothing needs to move.
 */
export function fitFor(aspect: number): number {
  return Math.max(1, 1.37 / Math.max(0.4, aspect));
}

export function systemSpan(planets: number): number {
  return ORBIT_INNER + Math.max(0, planets - 1) * ORBIT_STEP;
}

export function cameraDistance(span: number, aspect: number, zoom = 1): number {
  return Math.max(CAMERA_FLOOR, span * CAMERA_REACH * 1.55) * fitFor(aspect) * zoom;
}

/**
 * The furthest the belt ever reaches across the screen, in normalised device
 * coordinates: 1 is the edge of the window. Anything at or above 1 is off it.
 */
export function beltExtent(planets: number, aspect: number): { x: number; y: number } {
  const span = systemSpan(planets);
  const distance = cameraDistance(span, aspect);
  const half = Math.tan(((FIELD_OF_VIEW * Math.PI) / 180) / 2);

  const camera = [0, distance * CAMERA_LIFT, distance] as const;
  const length = Math.hypot(...camera);
  const forward = camera.map((v) => -v / length) as unknown as [number, number, number];
  const rightRaw = [forward[2], 0, -forward[0]] as const;
  const rightLength = Math.hypot(...rightRaw);
  const right = rightRaw.map((v) => v / rightLength) as unknown as [number, number, number];
  const up = [
    right[1] * forward[2] - right[2] * forward[1],
    right[2] * forward[0] - right[0] * forward[2],
    right[0] * forward[1] - right[1] * forward[0],
  ] as const;

  const radius = span * (BELT_INNER + BELT_SPREAD);
  const rise = span * (BELT_HEIGHT / 2 + BELT_BOB);
  let x = 0;
  let y = 0;
  for (const height of [-rise, rise]) {
    for (let i = 0; i < 360; i += 1) {
      const angle = (i / 360) * Math.PI * 2;
      const point = [Math.cos(angle) * radius, height, Math.sin(angle) * radius] as const;
      const v = [point[0] - camera[0], point[1] - camera[1], point[2] - camera[2]] as const;
      const depth = v[0] * forward[0] + v[1] * forward[1] + v[2] * forward[2];
      if (depth <= 0.01) return { x: Infinity, y: Infinity };
      x = Math.max(x, Math.abs((v[0] * right[0] + v[1] * right[1] + v[2] * right[2]) / (half * aspect * depth)));
      y = Math.max(y, Math.abs((v[0] * up[0] + v[1] * up[1] + v[2] * up[2]) / (half * depth)));
    }
  }
  return { x, y };
}
